import {identity} from 'lodash';
import React, {FC, ReactNode, useEffect, useState} from 'react';

import {usePrevious} from '../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {useInitialEffect} from '../../../../../libs/shared/core/src/helpers/react/hooks/useEffectHelpers';
import {ReplyFactModel} from '../../../../../libs/shared/core/src/models/api/factModel';
import {MessageModel} from '../../../../../libs/shared/core/src/models/api/messageModel';
import {getHasMacroRecommendations} from '../../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {
  EmlPanelId,
  MessagePanelId,
  ViewerPanelId,
} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {useAppDispatch, useAppSelector} from '../../../../helpers/appReduxHelpers';
import {
  ConversationOngoingTaskModel,
  OngoingTaskTypesEnum,
} from '../../../../models/api/conversationOngoingTaskModel';
import {MacroModel} from '../../../../models/api/macroModel';
import {fetchMacrosAsyncAction} from '../../../../store/data/macros/macroActions';
import {AiReplyButton} from './aiReplyButton';
import {MacroButton} from './macroButton';
import {OngoingTaskButton} from './ongoingTaskButton';
import {PickMacroDropdown} from './pickMacroDropdown';
import {useRunMacro} from './useRunMacro';

export interface MacroTriggersProps {
  message: MessageModel;
  aiReply?: ReplyFactModel;
  ongoingTask?: ConversationOngoingTaskModel;
  macros: ReadonlyArray<MacroModel>;
  panelId: ViewerPanelId | MessagePanelId | EmlPanelId;
  renderWrapper?: (children: ReactNode) => ReactNode;
}

export const MacroTriggers: FC<MacroTriggersProps> = ({
  message,
  aiReply,
  ongoingTask,
  macros,
  panelId,
  renderWrapper = identity,
}) => {
  const dispatch = useAppDispatch();
  const hasMacroRecommendations = useAppSelector(getHasMacroRecommendations);
  const previousOngoingTask = usePrevious(ongoingTask);

  // If the company does not have macro recommendations enabled, we simply suggest the first macro, or the second one if they have just run the first.
  const [suggestedMacroIndex, setSuggestedMacroIndex] = useState(0);

  const {onRunMacro, dialog} = useRunMacro(message.conversationId);

  //  If the company does not have macro recommendations enabled, suggest the second macro if the just removed ongoing task was first macro.
  useEffect(() => {
    if (
      hasMacroRecommendations ||
      !previousOngoingTask ||
      ongoingTask ||
      previousOngoingTask.workloadType !== OngoingTaskTypesEnum.MACRO
    ) {
      return;
    }

    if ('macroId' in previousOngoingTask) {
      setSuggestedMacroIndex(previousOngoingTask.macroId === macros[0].id && macros.length > 1 ? 1 : 0);
    }
  }, [hasMacroRecommendations, macros, ongoingTask, previousOngoingTask]);

  // Lazy-load macros.
  useInitialEffect(() => {
    dispatch(fetchMacrosAsyncAction());
  });

  const renderDropdown = (popoverProps: PopoverTransferProps) => (
    <PickMacroDropdown
      {...popoverProps}
      macros={macros}
      onRunMacro={onRunMacro}
      shouldRenderIcon={Boolean(aiReply)}
    />
  );

  const shouldRenderDropdown = (aiReply && macros.length > 0) || macros.length > 1;
  const maybeRenderDropdown = shouldRenderDropdown ? renderDropdown : undefined;

  const maybeRenderButton = () => {
    if (ongoingTask && ongoingTask.isInteractive) {
      return <OngoingTaskButton ongoingTask={ongoingTask} />;
    }

    if (aiReply) {
      return (
        <AiReplyButton
          aiReply={aiReply}
          message={message}
          renderDropdown={maybeRenderDropdown}
          panelId={panelId}
        />
      );
    }

    if (macros.length > 0) {
      // If there are 2 macros and the second is being recommended and it is deleted we need to show the first.
      const macro = macros[suggestedMacroIndex] ?? macros[0];
      return (
        <MacroButton
          macro={macro}
          renderDropdown={maybeRenderDropdown}
          onRunMacro={() => onRunMacro(macro)}
        />
      );
    }

    return null;
  };

  const button = maybeRenderButton();
  return button ? (
    <>
      {dialog}
      {renderWrapper(button)}
    </>
  ) : null;
};
